import { Component, OnInit, OnDestroy, Injectable, ChangeDetectionStrategy } from "@angular/core";
import { TrainOrderService } from "../../services/train-order.service";
import { TrainInformation } from "../../models/train-information.model";
import { AuthService } from "../../services/auth.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { userReadWriteGroups } from "../../../assets/user-groups";
import { ChipFilterGroup } from "../../shared/chip-filter/chip-filter-group";

@Injectable()
export class PaginatorIntl extends MatPaginatorIntl {
  override itemsPerPageLabel = 'Rader per sida:';
  override nextPageLabel = 'Nästa sida';
  override previousPageLabel = 'Föregående sida';
  override firstPageLabel = 'Första sidan';
  override lastPageLabel = 'Sista sidan';

  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    if (length === 0 || pageSize === 0) {
      return `0 av ${length}`;
    }
    const startIndex = page * pageSize;
    const endIndex = startIndex < length ?
      Math.min(startIndex + pageSize, length) :
      startIndex + pageSize;
    return `${startIndex + 1} - ${endIndex} av ${length}`;
  };
}

@Component({
  selector: "app-train-orders",
  standalone: false,
  templateUrl: "./train-orders.component.html",
  styleUrls: ["./train-orders.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [{ provide: MatPaginatorIntl, useClass: PaginatorIntl }]
})
export class TrainOrdersComponent implements OnInit, OnDestroy {
  trainInformations: TrainInformation[] = [];
  filteredTrainInformations: TrainInformation[] = [];
  paginatedTrainInformations: TrainInformation[] = [];
  loading: boolean = false;
  selectedDate: Date | null = new Date();

  // Pagination
  pageSize: number = 25;
  pageIndex: number = 0;
  pageSizeOptions: number[] = [10, 25, 50, 100];

  // Sorting
  sortColumn: 'otn' | 'driver' | 'originTime' | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  filters = new ChipFilterGroup<TrainInformation>([
    {
      key: 'driver',
      extract: i => [`${i.firstName} ${i.lastName}`.trim()].filter(Boolean)
    },
    {
      key: 'otn',
      extract: i => i.operationalTrainNumber ? [i.operationalTrainNumber] : [],
      sort: (a, b) => {
        const numA = parseInt(a);
        const numB = parseInt(b);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.localeCompare(b);
      }
    }
  ]);

  constructor(
    private trainOrderService: TrainOrderService,
    private authService: AuthService,
    private snackBar: MatSnackBar
  ) { }

  async ngOnInit() {
    await this.authService.hasGroupsAsync(userReadWriteGroups.TRAIN_ORDERS);
    this.selectedDate = new Date();
    this.fetchTrainInformations();
  }

  ngOnDestroy() {
  }

  fetchTrainInformations() {
    const selectedDate = this.selectedDate;
    if (!selectedDate) {
      this.showSnackbar('Välj ett driftdatum');
      return;
    }

    this.filters.clear();
    this.loading = true;
    const formattedDate = this.formatDate(selectedDate);

    this.trainOrderService.getTrainInformations(formattedDate)
      .subscribe({
        next: (data) => {
          this.trainInformations = data.map(item => {
            if (item.originTime) item.originTime = new Date(item.originTime);
            if (item.destinationTime) item.destinationTime = new Date(item.destinationTime);
            if (item.canceledAt) item.canceledAt = new Date(item.canceledAt);
            return item;
          });
          this.filters.populate(this.trainInformations);
          this.filteredTrainInformations = [...this.trainInformations];
          this.loading = false;
          this.updatePaginatedData();
        },
        error: (error) => {
          this.showSnackbar(`Kunde inte hämta tågorderuttag (${error.status})`);
          this.loading = false;
          this.paginatedTrainInformations = [];
        }
      });
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  applyFilters() {
    this.filteredTrainInformations = this.filters.apply(this.trainInformations);
    if (this.sortColumn) {
      this.applySorting();
    }
    this.pageIndex = 0;
    this.updatePaginatedData();
  }

  clearFilters() {
    this.filters.clear();
    this.applyFilters();
  }

  sortBy(column: 'otn' | 'driver' | 'originTime') {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.applySorting();
  }

  applySorting() {
    this.filteredTrainInformations = [...this.filteredTrainInformations].sort((a, b) => {
      let comparison = 0;

      switch (this.sortColumn) {
        case 'otn':
          const otnA = a.operationalTrainNumber || '';
          const otnB = b.operationalTrainNumber || '';
          comparison = otnA.localeCompare(otnB);
          break;
        case 'driver':
          const nameA = `${a.firstName} ${a.lastName}`.trim();
          const nameB = `${b.firstName} ${b.lastName}`.trim();
          comparison = nameA.localeCompare(nameB);
          break;
        case 'originTime':
          const timeA = a.originTime ? new Date(a.originTime).getTime() : 0;
          const timeB = b.originTime ? new Date(b.originTime).getTime() : 0;
          comparison = timeA - timeB;
          break;
      }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    this.updatePaginatedData();
  }

  updatePaginatedData() {
    const startIndex = this.pageIndex * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedTrainInformations = this.filteredTrainInformations.slice(startIndex, endIndex);
  }

  onPageChange(event: any) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePaginatedData();
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) {
      return 'unfold_more';
    }
    return this.sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  formatDateTime(date: Date | string | undefined): string {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatTime(date: Date | string | undefined): string {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleTimeString('sv-SE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  showSnackbar(message: string) {
    this.snackBar.open(message, 'Stäng', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      panelClass: "snackbar-container"
    });
  }
}
